import type { IExpenseItem } from '../pages/Dashboard/vehicles_management/Expenses';

/**
 * Tabela de KM 2026 - COOASGO (Veículos)
 *
 * Define o valor por KM baseado na faixa de quilometragem percorrida.
 * O valor diminui conforme a faixa de KM aumenta.
 *
 * Regra especial:
 * - Se o setor do usuário for "894511ff-ef17-4584-9e6e-1c66cb7660d3"
 *   E o KM percorrido estiver entre 2001 e 2050,
 *   o valor fixo é R$ 1,63.
 *
 * Para todos os outros casos, segue a tabela abaixo.
 *
 * Última atualização: 2026
 */

// Setor com taxa fixa especial (Diretoria)
export const SPECIAL_SECTOR_ID = '894511ff-ef17-4584-9e6e-1c66cb7660d3';
export const SPECIAL_SECTOR_KM_MIN = 2001;
export const SPECIAL_SECTOR_KM_MAX = 2050;
export const SPECIAL_SECTOR_RATE = 1.63;

// Usuários com taxa fixa especial por ID (time de vacina + diretoria)
// Sandro Strapason, Ernani Klauck e Elcio da Fonseca Cação
export const SPECIAL_FIXED_RATE_USER_IDS = [
  'acbf984f-5af2-4c7d-a461-e18a88979fed', // Sandro Strapason
  '1a01a5b1-87ea-488c-a277-f8b248e594a9', // Ernani Klauck
  '383f715d-0629-4654-ad39-ab9f6b3cd152', // Elcio da Fonseca Cação
];

/**
 * Tabela de faixas de KM e seus respectivos valores por KM.
 * Cada entrada: [km_inicio, km_fim, valor_por_km]
 *
 * Para adicionar/alterar faixas, basta editar este array.
 */
export const KM_RATE_TABLE: [number, number, number][] = [
  [1, 500, 1.91],
  [501, 1000, 1.81],
  [1001, 1500, 1.76],
  [1501, 2000, 1.71],
  [2001, 2500, 1.66],
  [2501, 3000, 1.62],
  [3001, 3500, 1.59],
  [3501, Infinity, 1.55],
];

/**
 * Verifica se o usuário pertence ao setor da diretoria.
 */
export function isDirectorSector(sectorId?: string): boolean {
  return sectorId === SPECIAL_SECTOR_ID;
}

/**
 * Verifica se o usuário está na lista de usuários com taxa fixa especial por ID.
 */
export function isFixedRateEligibleUser(userId?: string): boolean {
  return !!userId && SPECIAL_FIXED_RATE_USER_IDS.includes(userId);
}

/**
 * Retorna o valor por KM baseado na faixa de quilometragem percorrida.
 *
 * @param km - Quilometragem total percorrida
 * @param sectorId - ID do setor do usuário (opcional)
 * @param useDirectorRate - Se true, usa taxa fixa especial (R$ 1,63) quando elegível
 * @param userId - ID do usuário (opcional), usado para elegibilidade individual
 * @returns Valor por KM em reais
 */
export function getKmRate(
  km: number,
  sectorId?: string,
  useDirectorRate?: boolean,
  userId?: string,
): number {
  // Taxa fixa quando selecionada pelo usuário e ele é elegível (diretoria ou ID especial)
  if (
    useDirectorRate &&
    (isDirectorSector(sectorId) || isFixedRateEligibleUser(userId))
  ) {
    return SPECIAL_SECTOR_RATE;
  }

  // Buscar na tabela de faixas
  const entry = KM_RATE_TABLE.find(([min, max]) => km >= min && km <= max);

  if (entry) {
    return entry[2];
  }

  // Abaixo de 1: usar o primeiro valor
  return KM_RATE_TABLE[0][2];
}

/**
 * Calcula o valor total de quilometragem.
 *
 * @param km - Quilometragem percorrida
 * @param sectorId - ID do setor do usuário (opcional)
 * @returns Objeto com taxa aplicada e valor total
 */
export function calculateKmValue(
  km: number,
  sectorId?: string,
): { rate: number; total: number } {
  const rate = getKmRate(km, sectorId);
  return {
    rate,
    total: km * rate,
  };
}

/**
 * Valor padrão de KM (para compatibilidade com código legado).
 * Usar getKmRate() para cálculos precisos.
 */
export const KM_RATE_DEFAULT = 1.63;

/**
 * Verifica se um item de "Quilometragem adicional" usa a taxa fixa especial.
 *
 * Essa informação é gravada em `observations.km_rate_type` no momento da
 * criação do item (ver ModalAddNewExpense), apenas quando o usuário
 * seleciona a "Tabela Fixa" (diretoria ou usuários de
 * `SPECIAL_FIXED_RATE_USER_IDS`). Um item sem esse campo é considerado
 * "normal" — ou seja, sujeito à regra de agrupamento por faixa (ver
 * `recalculateKmGroupItems`).
 */
export function isFixedRateKmObservation(observations?: string): boolean {
  if (!observations) return false;
  try {
    const parsed = JSON.parse(observations);
    return !!parsed.km_rate_type;
  } catch {
    return false;
  }
}

/**
 * Regra de agrupamento de KM para consolidação de relatórios e para
 * relatórios com mais de um lançamento de "Quilometragem adicional".
 *
 * Itens com taxa fixa (diretoria / `SPECIAL_FIXED_RATE_USER_IDS`, marcados
 * via `isFixedRateKmObservation`) NUNCA entram nessa conta — continuam
 * usando R$ 1,63/km individualmente, exatamente como antes.
 *
 * Para os demais itens de KM do mesmo relatório, a regra de negócio é:
 * somar todo o KM do grupo, buscar UMA única faixa em `KM_RATE_TABLE` para
 * esse total, e aplicar essa mesma taxa a cada item do grupo — em vez de
 * cada item buscar sua própria faixa individualmente (o que fazia quase
 * toda viagem cair sempre na faixa mais alta, já que viagens isoladas
 * raramente passam de 500km).
 *
 * Chamar sempre que a lista de itens de um relatório mudar: ao
 * adicionar/remover um item (ModalAddNewExpense) e ao consolidar vários
 * relatórios em um só (ModalConsolidateExpenses).
 */
export function recalculateKmGroupItems(
  items: IExpenseItem[],
  sectorId?: string,
): IExpenseItem[] {
  const normalKmTotal = items.reduce((sum, item) => {
    if (
      item.expense_type === 'Quilometragem adicional' &&
      !isFixedRateKmObservation(item.observations)
    ) {
      return sum + (Number(item.additional_km) || 0);
    }
    return sum;
  }, 0);

  const groupRate = getKmRate(normalKmTotal, sectorId);

  return items.map(item => {
    if (
      item.expense_type !== 'Quilometragem adicional' ||
      isFixedRateKmObservation(item.observations)
    ) {
      return item;
    }

    const km = Number(item.additional_km) || 0;
    const amount = Number((km * groupRate).toFixed(2));

    // Se observations já era um JSON (formato normal), preserva os demais
    // campos (obs, displacement, etc.) e só atualiza km_rate. Se for texto
    // puro de um lançamento antigo (pré-formato JSON), preserva esse texto
    // em vez de descartá-lo.
    let obs: Record<string, unknown> = {};
    try {
      obs = item.observations ? JSON.parse(item.observations) : {};
    } catch {
      obs = item.observations ? { obs: item.observations } : {};
    }
    obs.km_rate = groupRate;

    return {
      ...item,
      amount,
      observations: JSON.stringify(obs),
    };
  });
}
